import {
  brouillonModification,
  ISSUES_ENVOI,
  formaterJour,
  heureAParis,
  instantDeParis,
  jourAParis,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  type Brouillon,
  corps,
  ErreurApi,
  type FicheBrouillon,
  lire,
  requetes,
  type VueBrouillons,
} from '../api'
import { Formulaire } from '../composants/Formulaire'
import { Icone } from '../composants/Icone'
import { formaterInstant } from '../composants/Historique'
import { Section, Vide } from '../composants/Section'
import { EntetePage, Onglets } from '../composants/Ui'
import {
  LIBELLES_ISSUE_ENVOI,
  LIBELLES_STATUT_BROUILLON,
  LIBELLES_TYPE_BROUILLON,
} from '../libelles'
import { confirmer } from '../composants/confirmation'

// Boîte d'envoi (N1) : courriels, courriers, consignes pour un agent navigateur et
// rendez-vous, préparés par l'assistant ou depuis une tâche. On relit, on valide, puis on
// agit soi-même (envoyer depuis Gmail ou sa messagerie, poster la lettre, lancer l'agent dans
// Claude) et on note que c'est fait. L'appli n'envoie rien : elle écrit un courriel validé
// dans les brouillons Gmail de la boîte choisie, à la demande, et pose dans l'agenda un
// rendez-vous validé par sa propriétaire.

function LigneBrouillon({ b }: { b: Brouillon }) {
  return (
    <Link to="/boite-envoi/$id" params={{ id: b.id }} className="ligne ligne-lien">
      <div className="ligne-contenu">
        <strong>{b.objet}</strong> <span className="badge">{LIBELLES_TYPE_BROUILLON[b.type]}</span>
        <span className="badge">{LIBELLES_STATUT_BROUILLON[b.statut]}</span>
        {b.cle?.startsWith('relance:') && <span className="badge">Relance</span>}
        <div className="discret">
          {[
            b.entite ?? 'Sans entité',
            b.type === 'rendez_vous'
              ? b.debut && `le ${formaterInstant(b.debut)}`
              : (b.destinataireTiers ?? b.destinataire),
            b.projet && `projet « ${b.projet} »`,
            b.tache && `tâche « ${b.tache} »`,
            `préparé ${b.viaAssistant ? 'par l’assistant' : `par ${b.auteur ?? 'quelqu’un'}`}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
    </Link>
  )
}

export function BoiteEnvoi({
  vue,
  onChangeVue,
}: {
  vue: VueBrouillons
  onChangeVue: (vue: VueBrouillons) => void
}) {
  const liste = useQuery(requetes.brouillons({ vue }))
  return (
    <main>
      <EntetePage surtitre="Préparé, à valider" titre="Boîte d’envoi" />
      <Onglets
        valeur={vue}
        onChange={onChangeVue}
        onglets={[
          { valeur: 'a_traiter', libelle: 'À traiter' },
          { valeur: 'faits', libelle: 'Faits ou abandonnés' },
          { valeur: 'tous', libelle: 'Tous' },
        ]}
      />
      <Section titre="Envois préparés">
        <p className="discret">
          Rien ne part d’ici : on relit, on valide, puis on envoie soi-même (Gmail ou messagerie,
          courrier, agent navigateur de Claude) et on note que c’est fait. Un courriel validé
          s’écrit dans les brouillons Gmail, pièces jointes comprises ; un rendez-vous validé est
          posé dans l’agenda choisi. Les relances des tâches qui attendent un tiers depuis plus de
          sept jours se préparent chaque matin.
        </p>
        {liste.isPending && <Vide>Chargement…</Vide>}
        {liste.data?.length === 0 && <Vide>Rien à traiter.</Vide>}
        {liste.data?.map((b) => (
          <LigneBrouillon key={b.id} b={b} />
        ))}
      </Section>
    </main>
  )
}

/** Au plus dix pièces jointes (schéma de l'envoi). */
const PIECES_MAX = 10

/**
 * Les pièces de l'envoi (le PDF d'un dossier) : jointes au brouillon Gmail d'un courriel ;
 * sinon, on les ouvre ou les télécharge ici pour les joindre soi-même. Tant qu'il n'est pas
 * fait, on en ajoute (pièces de son entité) ou on en retire : c'est une retouche, à revalider.
 */
function PiecesJointes({ b, modifiable = false }: { b: FicheBrouillon; modifiable?: boolean }) {
  const client = useQueryClient()
  const [ajout, setAjout] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  if (b.pieces.length === 0 && !modifiable) return null
  const changer = async (pieceIds: string[]) => {
    try {
      setErreur(null)
      await lire(api.brouillons[':id'].$patch({ param: { id: b.id }, json: { pieceIds } }))
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Pièces jointes non modifiées.')
    }
  }
  const libelle =
    b.type !== 'courriel'
      ? 'Pièces jointes'
      : b.boitesGmail.length > 0 || b.dansGmail
        ? 'Jointes au brouillon Gmail'
        : 'À joindre dans la messagerie'
  return (
    <div className="pieces-jointes">
      <Icone nom="attach_file" />
      <div>
        {b.pieces.length > 0 ? `${libelle} :` : 'Aucune pièce jointe.'}
        {b.pieces.map((p) => (
          <div key={p.id}>
            <a href={`/api/pieces/${p.id}/fichier`} target="_blank" rel="noreferrer">
              {p.nomFichier ?? 'Pièce'}
            </a>{' '}
            (
            <a href={`/api/pieces/${p.id}/fichier?telecharger=1`} download={p.nomFichier ?? true}>
              télécharger
            </a>
            )
            {modifiable && (
              <>
                {' '}
                <button
                  type="button"
                  className="bouton-discret"
                  onClick={() => void changer(b.pieceIds.filter((x) => x !== p.id))}
                >
                  Retirer
                </button>
              </>
            )}
          </div>
        ))}
        {modifiable && b.pieceIds.length < PIECES_MAX && !ajout && (
          <button type="button" className="bouton-discret" onClick={() => setAjout(true)}>
            Ajouter une pièce jointe
          </button>
        )}
        {ajout && (
          <AjoutPieceJointe
            b={b}
            onChoisir={async (id) => {
              await changer([...b.pieceIds, id])
              setAjout(false)
            }}
            onFermer={() => setAjout(false)}
          />
        )}
        {erreur && <p className="alerte">{erreur}</p>}
      </div>
    </div>
  )
}

/** Chercher une pièce de l'entité de l'envoi (fournisseur, numéro, nom du fichier…) à joindre. */
function AjoutPieceJointe({
  b,
  onChoisir,
  onFermer,
}: {
  b: FicheBrouillon
  onChoisir: (pieceId: string) => Promise<void>
  onFermer: () => void
}) {
  const [q, setQ] = useState('')
  const recherche = q.trim()
  const trouvees = useQuery({
    ...requetes.pieces({
      vue: 'toutes',
      q: recherche,
      ...(b.entiteId && { entiteId: b.entiteId }),
    }),
    enabled: recherche.length >= 2,
  })
  // Rien ne passe d'une entité à l'autre : le serveur le refuse aussi.
  const possibles = (trouvees.data ?? [])
    .filter((p) => p.entiteId === b.entiteId && !b.pieceIds.includes(p.id))
    .slice(0, 10)
  return (
    <div className="pile">
      <label className="champ">
        Pièce à joindre
        <input
          type="search"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Fournisseur, numéro, nom du fichier…"
        />
      </label>
      {recherche.length >= 2 && trouvees.isFetched && possibles.length === 0 && (
        <p className="discret">Aucune pièce de {b.entite ?? 'cette entité'} ne correspond.</p>
      )}
      {possibles.map((p) => (
        <div key={p.id} className="ligne">
          <div className="ligne-contenu">
            <strong>{p.nomFichier ?? 'Pièce'}</strong>
            <div className="discret">
              {[p.emetteur, p.numero, p.dateDocument && formaterJour(p.dateDocument)]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <button type="button" className="bouton" onClick={() => void onChoisir(p.id)}>
            Joindre
          </button>
        </div>
      ))}
      <button type="button" className="bouton-discret" onClick={onFermer}>
        Fermer
      </button>
    </div>
  )
}

/**
 * Le brouillon Gmail d'un courriel validé : écrit dans la boîte choisie (proposée : celle de la
 * réponse au destinataire, sinon la sienne), avec le texte entier et les pièces jointes ; on
 * l'envoie de Gmail. L'écrire de nouveau le remplace, retouches faites dans Gmail comprises.
 */
function BrouillonGmail({ b }: { b: FicheBrouillon }) {
  const client = useQueryClient()
  const [boiteId, setBoiteId] = useState(b.gmailBoiteId ?? b.boiteGmailProposee ?? '')
  const [enCours, setEnCours] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  if (b.boitesGmail.length === 0 && !b.dansGmail)
    return (
      <p className="discret">
        Pour l’écrire dans les brouillons Gmail avec ses pièces jointes : dans Réglages, « Boîtes
        mail », autoriser les brouillons d’une boîte.
      </p>
    )
  const ecrire = async () => {
    if (
      b.dansGmail &&
      !(await confirmer(
        'Remplacer le brouillon dans Gmail ? Ce qui y a été retouché depuis sera perdu.',
      ))
    )
      return
    try {
      setEnCours(true)
      setErreur(null)
      const r = await lire(
        api.brouillons[':id'].gmail.$post({ param: { id: b.id }, json: { boiteId } }),
      )
      setMessage(
        r.recree
          ? 'Le brouillon n’était plus dans Gmail (envoyé ou supprimé là-bas) : il y a été écrit de nouveau.'
          : null,
      )
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Brouillon non écrit dans Gmail.')
    } finally {
      setEnCours(false)
    }
  }
  return (
    <>
      {b.dansGmail ? (
        <p className="discret">
          Dans les brouillons de {b.gmailBoite ?? 'une boîte Gmail'}
          {b.gmailEcritLe && ` (écrit le ${formaterInstant(b.gmailEcritLe)})`} : l’ouvrir, le relire
          et l’envoyer de Gmail, puis noter ici que c’est fait. Retouché ici depuis : le mettre à
          jour dans Gmail.
        </p>
      ) : (
        b.boitesGmail.length > 1 && (
          <label className="champ">
            Boîte Gmail
            <select value={boiteId} onChange={(e) => setBoiteId(e.target.value)}>
              {!boiteId && <option value="">Choisir…</option>}
              {b.boitesGmail.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.adresse}
                </option>
              ))}
            </select>
          </label>
        )
      )}
      <div className="boutons">
        {b.gmailLien && (
          <a
            className="bouton bouton-principal"
            href={b.gmailLien}
            target="_blank"
            rel="noreferrer"
          >
            Ouvrir dans Gmail
          </a>
        )}
        <button
          type="button"
          className={b.dansGmail ? 'bouton' : 'bouton bouton-principal'}
          disabled={!boiteId || enCours}
          onClick={() => void ecrire()}
        >
          {enCours
            ? 'Écriture…'
            : b.dansGmail
              ? 'Mettre à jour le brouillon Gmail'
              : b.boitesGmail.length === 1
                ? `Écrire le brouillon dans ${b.boitesGmail[0]!.adresse}`
                : 'Écrire le brouillon dans Gmail'}
        </button>
      </div>
      {message && <p className="discret">{message}</p>}
      {erreur && <p className="alerte">{erreur}</p>}
    </>
  )
}

function Copier({ texte, libelle }: { texte: string; libelle: string }) {
  const [copie, setCopie] = useState(false)
  return (
    <button
      type="button"
      className="bouton"
      onClick={async () => {
        await navigator.clipboard.writeText(texte)
        setCopie(true)
        setTimeout(() => setCopie(false), 2000)
      }}
    >
      {copie ? 'Copié' : libelle}
    </button>
  )
}

type Suppression = ReturnType<typeof useSuppression>

/**
 * Supprimer un envoi pas encore fait, confirmé dans la page (le serveur refuse le reste : fait,
 * un envoi reste comme trace ; un rendez-vous dans l'agenda s'abandonne d'abord).
 */
function useSuppression(b: FicheBrouillon, apres?: () => void) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const possible = b.statut !== 'fait' && !b.evenementPoseLe && !b.dansGmail
  const supprimer = async () => {
    if (!(await confirmer(`Supprimer « ${b.objet} » de la boîte d’envoi ?`))) return
    try {
      setErreur(null)
      await lire(api.brouillons[':id'].$delete({ param: { id: b.id } }))
      client.removeQueries({ queryKey: requetes.brouillon(b.id).queryKey })
      apres?.()
      await client.invalidateQueries({ queryKey: ['brouillons'] })
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Suppression impossible.')
    }
  }
  return { possible, supprimer, erreur }
}

function BoutonSupprimer({ s }: { s: Suppression }) {
  if (!s.possible) return null
  return (
    <button type="button" className="bouton-discret" onClick={() => void s.supprimer()}>
      Supprimer
    </button>
  )
}

function Actions({ b, apresSuppression }: { b: FicheBrouillon; apresSuppression?: () => void }) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const [compteRendu, setCompteRendu] = useState('')
  const suppression = useSuppression(b, apresSuppression)
  const agir = async (action: 'valider' | 'fait' | 'abandonner' | 'rouvrir') => {
    if (
      action === 'abandonner' &&
      b.dansGmail &&
      !(await confirmer(
        `Abandonner « ${b.objet} » et le retirer des brouillons de ${b.gmailBoite ?? 'Gmail'} ?`,
      ))
    )
      return
    try {
      setErreur(null)
      await lire(
        api.brouillons[':id'].action.$post({
          param: { id: b.id },
          json: { action, ...(action === 'fait' && compteRendu.trim() && { compteRendu }) },
        }),
      )
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }
  if (b.type === 'rendez_vous')
    return (
      <ActionsRendezVous
        b={b}
        agir={agir}
        erreur={erreur ?? suppression.erreur}
        suppression={suppression}
      />
    )
  // Un courriel déjà dans les brouillons Gmail (écrit par l'assistant avant toute validation,
  // ou retouché depuis) se relit et s'envoie de Gmail : l'envoi de là vaut validation.
  const aEnvoyer = b.statut === 'valide' || (b.statut === 'brouillon' && b.dansGmail)
  return (
    <Section titre="Agir">
      {b.statut === 'brouillon' && !aEnvoyer && (
        <>
          <p className="discret">Relire, retoucher si besoin, puis valider.</p>
          <div className="boutons">
            <button
              type="button"
              className="bouton bouton-principal"
              onClick={() => void agir('valider')}
            >
              Valider
            </button>
            <button
              type="button"
              className="bouton-discret"
              onClick={() => void agir('abandonner')}
            >
              Abandonner
            </button>
            <BoutonSupprimer s={suppression} />
          </div>
        </>
      )}
      {aEnvoyer && (
        <>
          {b.type === 'courriel' && <BrouillonGmail b={b} />}
          <div className="boutons">
            {b.type === 'courriel' && b.mailto && !b.dansGmail && (
              <a
                className={b.boitesGmail.length > 0 ? 'bouton-discret' : 'bouton bouton-principal'}
                href={b.mailto}
              >
                Ouvrir dans ma messagerie
              </a>
            )}
            {b.type === 'courriel' && <Copier texte={b.corps} libelle="Copier le texte" />}
            {b.type === 'courrier' && (
              <a
                className="bouton bouton-principal"
                href={`/api/brouillons/${b.id}/lettre`}
                target="_blank"
                rel="noreferrer"
              >
                Télécharger la lettre (PDF)
              </a>
            )}
            {b.type === 'navigation' && (
              <Copier texte={b.corps} libelle="Copier le prompt pour Claude" />
            )}
          </div>
          {b.type === 'navigation' && (
            <p className="discret">
              Dans Claude desktop, avec le navigateur (Claude in Chrome ou navigateur intégré) :
              coller le prompt. L’agent s’arrête avant tout geste qui paie, déclare ou signe.
            </p>
          )}
          <label className="champ">
            Compte rendu (facultatif)
            <textarea
              rows={2}
              value={compteRendu}
              onChange={(e) => setCompteRendu(e.target.value)}
              placeholder="Accusé, numéro, remarque…"
            />
          </label>
          <div className="boutons">
            <button type="button" className="bouton" onClick={() => void agir('fait')}>
              C’est fait
            </button>
            <button
              type="button"
              className="bouton-discret"
              onClick={() => void agir('abandonner')}
            >
              {b.dansGmail ? 'Abandonner et retirer de Gmail' : 'Abandonner'}
            </button>
            <BoutonSupprimer s={suppression} />
          </div>
        </>
      )}
      {b.statut === 'abandonne' && (
        <div className="boutons">
          <button type="button" className="bouton" onClick={() => void agir('rouvrir')}>
            Rouvrir
          </button>
          <BoutonSupprimer s={suppression} />
        </div>
      )}
      {b.statut === 'fait' && (
        <p className="discret">
          Fait le {b.faitLe ? formaterInstant(b.faitLe) : '?'}
          {b.compteRendu && ` : ${b.compteRendu}`}
        </p>
      )}
      {(erreur ?? suppression.erreur) && <p className="alerte">{erreur ?? suppression.erreur}</p>}
    </Section>
  )
}

/**
 * Rendez-vous : la propriétaire de l'agenda valide, l'appli le pose dans son calendrier
 * principal ; retouché, il reste dans l'agenda jusqu'à la validation suivante ; abandonné,
 * il en est retiré.
 */
function ActionsRendezVous({
  b,
  agir,
  erreur,
  suppression,
}: {
  b: FicheBrouillon
  agir: (action: 'valider' | 'abandonner' | 'rouvrir') => Promise<void>
  erreur: string | null
  suppression: Suppression
}) {
  const retirer = async () => {
    if (await confirmer(`Retirer « ${b.objet} » de l’agenda de ${b.agenda ?? '?'} ?`))
      void agir('abandonner')
  }
  return (
    <Section titre="Agir">
      {b.creneau && (
        <p>
          <strong>{b.creneau}</strong>
          {b.lieu && ` · ${b.lieu}`}
        </p>
      )}
      <p className="discret">
        {b.agenda ? `Agenda : ${b.agenda} (calendrier principal)` : 'Aucun agenda choisi.'}
        {b.invites.length > 0 && ` · invités : ${b.invites.join(', ')} (Google les prévient)`}
      </p>
      {b.statut === 'brouillon' && (
        <>
          <p className="discret">
            {b.evenementPoseLe
              ? 'Retouché : l’agenda garde l’ancienne version jusqu’à la validation, qui le met à jour.'
              : 'Relire, retoucher si besoin, puis valider : l’appli le pose dans l’agenda. Seule la propriétaire de l’agenda valide.'}
          </p>
          <div className="boutons">
            <button
              type="button"
              className="bouton bouton-principal"
              disabled={!b.agendaBoiteId}
              onClick={() => void agir('valider')}
            >
              {b.evenementPoseLe
                ? 'Valider et mettre à jour l’agenda'
                : 'Valider et poser dans l’agenda'}
            </button>
            <button
              type="button"
              className="bouton-discret"
              onClick={() => (b.evenementPoseLe ? retirer() : void agir('abandonner'))}
            >
              {b.evenementPoseLe ? 'Abandonner et retirer de l’agenda' : 'Abandonner'}
            </button>
            <BoutonSupprimer s={suppression} />
          </div>
          {!b.agendaBoiteId && (
            <p className="discret">
              Choisir l’agenda ci-dessous. Aucun ? Dans Réglages, « Boîtes mail », connecter sa
              boîte Gmail en autorisant l’agenda.
            </p>
          )}
        </>
      )}
      {b.statut === 'fait' && (
        <>
          <p className="discret">
            Posé le {b.faitLe ? formaterInstant(b.faitLe) : '?'} dans l’agenda de {b.agenda ?? '?'}.
            Le retoucher ci-dessous le renvoie en relecture.
          </p>
          <div className="boutons">
            {b.evenementLien && (
              <a
                className="bouton bouton-principal"
                href={b.evenementLien}
                target="_blank"
                rel="noreferrer"
              >
                Ouvrir dans Google Agenda
              </a>
            )}
            <button type="button" className="bouton-discret" onClick={retirer}>
              Retirer de l’agenda
            </button>
          </div>
        </>
      )}
      {b.statut === 'abandonne' && (
        <div className="boutons">
          <button type="button" className="bouton" onClick={() => void agir('rouvrir')}>
            Rouvrir
          </button>
          <BoutonSupprimer s={suppression} />
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </Section>
  )
}

/** Contenu d'un rendez-vous : titre, jour et heures de Paris, lieu, invités, agenda, description. */
function ContenuRendezVous({ b }: { b: FicheBrouillon }) {
  const client = useQueryClient()
  const boites = useQuery(requetes.boites)
  const agendas = (boites.data ?? []).filter((x) => x.agenda && x.etat === 'active')
  if (b.agendaBoiteId && b.agenda && !agendas.some((a) => a.id === b.agendaBoiteId))
    agendas.push({ id: b.agendaBoiteId, adresse: b.agenda } as (typeof agendas)[number])
  const debut = b.debut ? new Date(b.debut) : null
  const fin = b.fin ? new Date(b.fin) : null
  return (
    <Formulaire
      key={`${b.majLe as unknown as string}-${agendas.length}`}
      champs={[
        { nom: 'objet', libelle: 'Titre', type: 'texte', requis: true },
        { nom: 'jour', libelle: 'Jour', type: 'date', requis: true },
        { nom: 'heureDebut', libelle: 'Début (heure de Paris)', type: 'heure', requis: true },
        { nom: 'heureFin', libelle: 'Fin', type: 'heure', requis: true },
        { nom: 'lieu', libelle: 'Lieu', type: 'texte' },
        {
          nom: 'agendaBoiteId',
          libelle: 'Agenda (calendrier principal de la boîte)',
          type: 'choix',
          options: agendas.map((a) => ({ valeur: a.id, libelle: a.adresse })),
        },
        {
          nom: 'invites',
          libelle: 'Invités (une adresse par ligne)',
          type: 'liste',
          aide: 'Des personnes de l’appli seulement : Google leur envoie l’invitation.',
        },
        { nom: 'corps', libelle: 'Description', type: 'zone', lignes: 8, requis: true },
      ]}
      initial={{
        ...b,
        jour: debut && jourAParis(debut),
        heureDebut: debut && heureAParis(debut),
        heureFin: fin && heureAParis(fin),
        agendaBoiteId: b.agendaBoiteId ?? '',
      }}
      libelleBouton={b.statut === 'fait' ? 'Retoucher (à revalider)' : 'Enregistrer'}
      onValider={async ({ jour, heureDebut, heureFin, agendaBoiteId, ...v }) => {
        // Fin avant le début (rendez-vous qui passe minuit) : le lendemain.
        const debut = instantDeParis(jour as string, heureDebut as string)
        let fin = instantDeParis(jour as string, heureFin as string)
        if (Date.parse(fin) <= Date.parse(debut))
          fin = new Date(Date.parse(fin) + 24 * 3600_000).toISOString()
        await lire(
          api.brouillons[':id'].$patch({
            param: { id: b.id },
            json: corps(brouillonModification, {
              ...v,
              debut,
              fin,
              ...((agendaBoiteId || null) !== b.agendaBoiteId && {
                agendaBoiteId: agendaBoiteId || null,
              }),
            }),
          }),
        )
        await client.invalidateQueries()
      }}
    />
  )
}

export function BrouillonFiche({ id }: { id: string }) {
  const fiche = useQuery(requetes.brouillon(id))
  const naviguer = useNavigate()
  const retour = () => void naviguer({ to: '/boite-envoi' })
  if (fiche.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (!fiche.data)
    return (
      <main>
        <p className="alerte">Envoi introuvable.</p>
        <Link to="/boite-envoi">Retour à la boîte d’envoi</Link>
      </main>
    )
  const b = fiche.data
  // Préparé pour une tâche (le mail d'un dossier, une démarche) : il se relit et se valide
  // dans cette tâche ; ici, on le voit seulement.
  const dansSaTache = b.tacheId !== null
  return (
    <main>
      <header className="entete-fiche">
        <p className="fil">
          <Link to="/boite-envoi">Boîte d’envoi</Link>
          {b.projetId && (
            <>
              {' · '}
              <Link to="/projets/$id" params={{ id: b.projetId }} search={{ onglet: 'envois' }}>
                {b.projet}
              </Link>
            </>
          )}
          {b.tacheId && (
            <>
              {' · '}
              <Link to="/taches/$id" params={{ id: b.tacheId }}>
                {b.tache}
              </Link>
            </>
          )}
        </p>
        <h1>{b.objet}</h1>
        <p>
          <span className="badge">{LIBELLES_TYPE_BROUILLON[b.type]}</span>
          <span className="badge">{LIBELLES_STATUT_BROUILLON[b.statut]}</span>
          {b.viaAssistant && <span className="badge">Préparé par l’assistant</span>}
        </p>
      </header>
      {dansSaTache ? (
        <>
          <AgirDansSaTache b={b} apresSuppression={retour} />
          <Section titre={b.type === 'navigation' ? 'Prompt' : 'Contenu'}>
            {b.type === 'rendez_vous' && b.debut && <p>Le {formaterInstant(b.debut)}</p>}
            <pre className="texte-brut">{b.corps}</pre>
            <PiecesJointes b={b} />
          </Section>
        </>
      ) : (
        <RelectureBrouillon b={b} apresSuppression={retour} />
      )}
    </main>
  )
}

/** Envoi préparé pour une tâche, vu de la boîte d'envoi : l'ouvrir dans sa tâche, ou le supprimer. */
function AgirDansSaTache({
  b,
  apresSuppression,
}: {
  b: FicheBrouillon
  apresSuppression: () => void
}) {
  const suppression = useSuppression(b, apresSuppression)
  return (
    <Section titre="Agir">
      <p className="discret">
        Préparé pour une tâche : il se relit et se valide dans la tâche.
        {b.issueTache &&
          b.issueTache !== 'rien' &&
          (b.statut === 'brouillon' || b.statut === 'valide') && (
            <> Quand il partira : {LIBELLES_ISSUE_ENVOI[b.issueTache].toLowerCase()}.</>
          )}
      </p>
      <div className="boutons">
        <Link to="/taches/$id" params={{ id: b.tacheId! }} className="bouton bouton-principal">
          Ouvrir la tâche
        </Link>
        <BoutonSupprimer s={suppression} />
      </div>
      {suppression.erreur && <p className="alerte">{suppression.erreur}</p>}
    </Section>
  )
}

/**
 * Ce que devient la tâche quand l'envoi part : proposé par Claude en le rédigeant, choisi ici
 * (sans retoucher le texte, la validation reste), appliqué au « fait », noté à la main ou vu
 * dans les envoyés de Gmail.
 */
function IssueTache({ b, modifiable }: { b: FicheBrouillon; modifiable: boolean }) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const issue = b.issueTache ?? 'rien'
  return (
    <div className="issue-tache">
      <label className="filtre-pour">
        <span className="discret">Quand il part</span>
        <select
          value={issue}
          disabled={!modifiable}
          aria-label="Ce que devient la tâche quand l’envoi part"
          onChange={async (e) => {
            setErreur(null)
            try {
              await lire(
                api.brouillons[':id'].$patch({
                  param: { id: b.id },
                  json: corps(brouillonModification, { issueTache: e.target.value }),
                }),
              )
              await client.invalidateQueries({ queryKey: ['brouillons'] })
            } catch (x) {
              setErreur((x as Error).message)
            }
          }}
        >
          {ISSUES_ENVOI.map((i) => (
            <option key={i} value={i}>
              {LIBELLES_ISSUE_ENVOI[i]}
            </option>
          ))}
        </select>
      </label>
      {b.statut === 'fait' && b.gmailEnvoyeLe && (
        <span className="discret">Vu parti dans Gmail le {formaterInstant(b.gmailEnvoyeLe)}.</span>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}

/**
 * Relire, retoucher et valider un envoi préparé, puis noter qu'il est fait : sur sa fiche
 * pour un envoi libre, dans sa tâche pour un envoi préparé depuis une tâche.
 */
export function RelectureBrouillon({
  b,
  apresSuppression,
}: {
  b: FicheBrouillon
  apresSuppression?: () => void
}) {
  const client = useQueryClient()
  const id = b.id
  const modifiable =
    b.statut === 'brouillon' ||
    b.statut === 'valide' ||
    (b.type === 'rendez_vous' && b.statut === 'fait')
  return (
    <>
      <Actions b={b} apresSuppression={apresSuppression} />
      {(b.type === 'courriel' || b.type === 'courrier') && b.tacheId && (
        <IssueTache b={b} modifiable={b.statut === 'brouillon' || b.statut === 'valide'} />
      )}
      <Section titre={b.type === 'navigation' ? 'Prompt' : 'Contenu'}>
        {b.type === 'rendez_vous' && modifiable ? (
          <ContenuRendezVous b={b} />
        ) : modifiable ? (
          <Formulaire
            key={b.majLe as unknown as string}
            champs={[
              {
                nom: 'destinataire',
                libelle:
                  b.type === 'courriel'
                    ? 'Destinataire (adresse mail)'
                    : b.type === 'courrier'
                      ? 'Destinataire (nom et adresse postale, une ligne par ligne)'
                      : 'Site de départ',
                type: b.type === 'courrier' ? 'zone' : 'texte',
                ...(b.type === 'courrier' && { lignes: 4 }),
              },
              ...(b.type === 'courriel'
                ? [
                    {
                      nom: 'copie',
                      libelle: 'Copie (une adresse par ligne)',
                      type: 'liste' as const,
                    },
                  ]
                : []),
              { nom: 'objet', libelle: 'Objet', type: 'texte', requis: true },
              { nom: 'corps', libelle: 'Texte', type: 'zone', lignes: 14, requis: true },
            ]}
            initial={b}
            libelleBouton={b.statut === 'valide' ? 'Retoucher (à revalider)' : 'Enregistrer'}
            onValider={async (v) => {
              await lire(
                api.brouillons[':id'].$patch({
                  param: { id },
                  json: corps(brouillonModification, v),
                }),
              )
              await client.invalidateQueries()
            }}
          />
        ) : (
          <pre className="texte-brut">{b.corps}</pre>
        )}
        <PiecesJointes b={b} modifiable={modifiable && b.type !== 'rendez_vous'} />
      </Section>
    </>
  )
}

/**
 * Un envoi préparé pour une tâche, relu, retouché et validé sur place : dans sa tâche, ou dans
 * l'onglet Envois de son projet (`avecTache` : le lien vers la tâche en plus).
 */
export function EnvoiDansLaTache({ id, avecTache = false }: { id: string; avecTache?: boolean }) {
  const fiche = useQuery(requetes.brouillon(id))
  if (!fiche.data) return <Vide>{fiche.isPending ? 'Chargement…' : 'Envoi introuvable.'}</Vide>
  const b = fiche.data
  return (
    <div className="pile">
      <RelectureBrouillon b={b} />
      <div className="boutons">
        {avecTache && b.tacheId && (
          <Link to="/taches/$id" params={{ id: b.tacheId }} className="bouton-discret">
            Ouvrir la tâche
          </Link>
        )}
        <Link to="/boite-envoi/$id" params={{ id }} className="bouton-discret">
          Voir dans la boîte d’envoi
        </Link>
      </div>
    </div>
  )
}
